import { useState } from "react";
import { QueryState } from "./useQueryState";

type CombinedData<S extends Record<string, QueryState<unknown>>> = {
  [K in keyof S]: Extract<S[K], { status: "success" }>["data"];
};

// Combines several query states into one: any error wins, then loading, then success.
export function useCombinedQueryState<
  S extends Record<string, QueryState<unknown>>,
>(states: S): QueryState<CombinedData<S>> {
  const [cache, setCache] = useState(() => ({
    result: combineQueryStates(states),
    states,
  }));
  if (!shallowEqual(cache.states, states)) {
    const next = { result: combineQueryStates(states), states };
    setCache(next);
    return next.result;
  }
  return cache.result;
}

function combineQueryStates<S extends Record<string, QueryState<unknown>>>(
  states: S,
): QueryState<CombinedData<S>> {
  const entries = Object.entries(states);
  const errors = entries
    .map(([, state]) => state)
    .filter((state) => state.status === "error");

  if (errors.length) {
    return {
      error: errors[0]?.error,
      messages: errors.flatMap((state) => state.messages),
      retry: () => errors.forEach((state) => state.retry()),
      status: "error",
    };
  }

  if (entries.some(([, state]) => state.status === "loading")) {
    return { status: "loading" };
  }

  return {
    data: Object.fromEntries(
      entries.map(([key, state]) => [
        key,
        state.status === "success" ? state.data : undefined,
      ]),
    ) as CombinedData<S>,
    status: "success",
  };
}

function shallowEqual(a: Record<string, unknown>, b: Record<string, unknown>) {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => Object.is(a[key], b[key]))
  );
}
